import { useState, useEffect } from "react"
import { RefreshCw, Clock, CheckCircle, XCircle, Package, Mail, ArrowRight, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"

// Icon mapping
const iconMap = {
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Mail,
}

const defaultRefundSteps = [
    {
      step: 1,
      title: "Contact Us",
      description: "Reach out within 30 days of purchase",
      icon: Mail,
    },
    {
      step: 2,
      title: "Return Authorization",
      description: "We'll provide return instructions",
      icon: CheckCircle,
    },
    {
      step: 3,
      title: "Ship Product",
      description: "Send the product back in original packaging",
      icon: Package,
    },
    {
      step: 4,
      title: "Refund Processed",
      description: "Refund issued within 5-7 business days",
      icon: RefreshCw,
    },
  ]

  const defaultEligibleItems = [
    "Unopened skincare products in original packaging",
    "Products that caused allergic reactions (with proof)",
    "Defective or damaged products upon arrival",
    "Wrong items sent due to our error",
  ]

  const defaultNonEligibleItems = [
    "Products opened and used for more than 7 days",
    "Items without original packaging or labels",
    "Products purchased more than 30 days ago",
    "Gift cards and promotional items",
  ]

export default function RefundPolicy() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get("/legal/refund")
        setPageData(res.data.page)
      } catch (error) {
        console.error("Error fetching refund policy:", error)
        setPageData(null)
      } finally {
        setLoading(false)
      }
    }
    fetchPage()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  // Extract data from pageData or use defaults
  const refundStepsSection = pageData?.sections?.find(s => s.title === "Refund Process")
  const refundSteps = refundStepsSection?.steps || defaultRefundSteps
  const eligibleSection = pageData?.sections?.find(s => s.title === "Eligible Items")
  const eligibleItems = eligibleSection?.items || defaultEligibleItems
  const nonEligibleSection = pageData?.sections?.find(s => s.title === "Non-Eligible Items")
  const nonEligibleItems = nonEligibleSection?.items || defaultNonEligibleItems
  const importantInfo = pageData?.additional_content?.important_info || []
  const specialCases = pageData?.additional_content?.special_cases || []
  
  const heroTitle = pageData?.hero_title || "Refund Policy"
  const heroSubtitle = pageData?.hero_subtitle || "We stand behind our products with a 30-day satisfaction guarantee."
  const heroBadgeText = pageData?.hero_badge_text || "30-Day Guarantee"
  
  // Format lastUpdated to show only date in "December 15, 2024" format
  const formatDate = (dateString) => {
    if (!dateString) return "December 15, 2024"
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString // Return as-is if invalid date
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateString || "December 15, 2024"
    }
  }
  const lastUpdated = formatDate(pageData?.last_updated)
  const introductionTitle = pageData?.introduction_title || "Our Commitment to You"
  const introductionContent = pageData?.introduction_content || "At Fayoz, we want you to be completely satisfied with your purchase."
  const contactTitle = pageData?.contact_title || "Need to Return Something?"
  const contactSubtitle = pageData?.contact_subtitle || "Our customer service team is here to help make your return process as smooth as possible."
  const contactEmail = pageData?.contact_email || "returns@fayozltd.com"

  return (
    <>
      <Seo {...routeSeoConfig.refund} type="website" />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {heroBadgeText && (
              <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <RefreshCw className="w-4 h-4" />
                {heroBadgeText}
              </div>
            )}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 text-balance">
              {heroTitle}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto text-pretty">
              {heroSubtitle}
            </p>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <Card className="mb-12 border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{introductionTitle}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {introductionContent}
                </p>
              </CardContent>
            </Card>

            {/* Refund Process */}
            <Card className="mb-12 border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900 text-center mb-4">How to Request a Refund</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-6">
                  {refundSteps.map((step, index) => {
                    const IconComponent = iconMap[step.icon] || RefreshCw
                    return (
                      <div key={index} className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                        <div className="text-sm font-semibold text-primary mb-2">Step {step.step}</div>
                        <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-600">{step.description}</p>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Eligible vs Non-Eligible Items */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-xl text-gray-900">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    Eligible for Refund
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {eligibleItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-xl text-gray-900">
                    <XCircle className="w-6 h-6 text-red-500" />
                    Not Eligible for Refund
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {nonEligibleItems.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Important Information */}
            {importantInfo.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {importantInfo.map((info, index) => {
                  const IconComponent = iconMap[info.icon] || Clock
                  return (
                    <Card key={index} className="border-0 shadow-lg bg-gradient-to-br from-primary/5 to-secondary/5">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg text-gray-900">
                          <IconComponent className="w-5 h-5 text-primary" />
                          {info.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600 text-sm">{info.content}</p>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            )}

            {/* Special Cases */}
            {specialCases.length > 0 && (
              <Card className="mb-12 border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">Special Circumstances</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {specialCases.map((specialCase, index) => (
                    <div key={index}>
                      <h4 className="font-semibold text-gray-900 mb-2">{specialCase.title}</h4>
                      <p className="text-gray-600 text-sm">{specialCase.content}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Contact Section */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Mail className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{contactTitle}</h3>
                {contactSubtitle && (
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    {contactSubtitle}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact-us">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                      Start Return Process
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  {contactEmail && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-primary text-primary hover:bg-primary/5 bg-transparent"
                    >
                      Email: {contactEmail}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
